<script>
/**
 * @component DispatchTaskModal
 * @description Modal para que el admin dispare manualmente una task
 *              vital/checkin/medication específica al paciente vía
 *              WhatsApp. Sirve para testing y para casos puntuales
 *              donde el admin quiere forzar el envío fuera del cron.
 *
 *              Flujo: selecciona paciente (autocomplete) → tipo de
 *              tarea (radio) → tarea específica (select filtrado) →
 *              confirma con warning de "esto manda WhatsApp real".
 *
 *              Resultado emite `dispatched` con { notificationId,
 *              interactionId, conversationId } para que el parent
 *              refresque el panel de auditoría.
 *
 * @example
 *   <DispatchTaskModal
 *     :visible.sync="dispatchModalVisible"
 *     @dispatched="onTaskDispatched"
 *   />
 */
import whatsappConfigService from "@/services/whatsappConfig";

export default {
  name: "DispatchTaskModal",

  props: {
    /** Visibilidad sync-eable con .sync */
    visible: { type: Boolean, default: false },
  },

  data() {
    return {
      // Búsqueda paciente
      patientLoading: false,
      patientOptions: [],
      selectedPatient: null, // { _id, firstName, lastName, phone }

      // Tipo de tarea
      taskType: null, // 'vital' | 'checkin' | 'medication'

      // Lista de tareas pendientes filtrada por tipo + paciente
      tasksLoading: false,
      taskOptions: [],
      selectedTaskId: null,

      // Dispatch en curso
      dispatching: false,
    };
  },

  computed: {
    /**
     * @description Mapping tipo → label legible para el radio.
     */
    taskTypeOptions() {
      return [
        { value: "vital", label: "Vital" },
        { value: "checkin", label: "Check-in (encuesta)" },
        { value: "medication", label: "Medicación" },
        // UC-REG-062: nuevo tipo de task disparable.
        { value: "health-conversation", label: "Conversación de salud (IA)" },
      ];
    },

    /**
     * @description Mapping tipo → nombre de modelo Mongoose en API.
     */
    taskModelFromType() {
      const map = {
        vital: "VitalTask",
        checkin: "CheckinTask",
        medication: "MedicationTask",
        "health-conversation": "HealthConversationTask",
      };
      return map[this.taskType] || null;
    },

    /**
     * @description Habilita el botón "Disparar" solo si hay paciente +
     *              tipo + tarea seleccionados Y no estamos disparando ya.
     */
    canDispatch() {
      return (
        !!this.selectedPatient &&
        !!this.taskType &&
        !!this.selectedTaskId &&
        !this.dispatching
      );
    },
  },

  watch: {
    /**
     * @description Cuando cambia el tipo de tarea, refresh de la lista
     *              de tareas pendientes del paciente para ese tipo.
     *              Si no hay paciente aún, no busca.
     */
    taskType() {
      this.selectedTaskId = null;
      this.taskOptions = [];
      if (this.selectedPatient && this.taskType) {
        this.loadPendingTasks();
      }
    },

    /**
     * @description Cuando cambia el paciente, resetea tipo y tareas para
     *              que el admin elija de nuevo.
     */
    selectedPatient() {
      this.taskType = null;
      this.selectedTaskId = null;
      this.taskOptions = [];
    },

    /**
     * @description Al abrir el modal limpia el estado anterior.
     */
    visible(val) {
      if (val) this.reset();
    },
  },

  methods: {
    /**
     * @description Limpia todo el estado del modal.
     */
    reset() {
      this.patientOptions = [];
      this.selectedPatient = null;
      this.taskType = null;
      this.taskOptions = [];
      this.selectedTaskId = null;
      this.dispatching = false;
    },

    /**
     * @description Callback del autocomplete de pacientes. Busca por
     *              nombre/teléfono (mín. 2 chars), debounce gestionado
     *              por el componente el-select remote.
     * @param {string} query
     */
    async onPatientSearch(query) {
      if (!query || query.trim().length < 2) {
        this.patientOptions = [];
        return;
      }
      this.patientLoading = true;
      try {
        const { data } = await whatsappConfigService.searchPatientsForDispatch(
          query.trim()
        );
        this.patientOptions = data.items || [];
      } catch (e) {
        this.$message.error("No se pudieron buscar pacientes");
      } finally {
        this.patientLoading = false;
      }
    },

    /**
     * @description Carga las tareas pendientes del paciente para el tipo
     *              seleccionado.
     */
    async loadPendingTasks() {
      if (!this.selectedPatient || !this.taskType) return;
      this.tasksLoading = true;
      this.selectedTaskId = null;
      try {
        const { data } =
          await whatsappConfigService.listPendingTasksForDispatch(
            this.selectedPatient._id,
            this.taskType
          );
        this.taskOptions = data.items || [];
      } catch (e) {
        this.$message.error("No se pudieron listar las tareas pendientes");
      } finally {
        this.tasksLoading = false;
      }
    },

    /**
     * @description Etiqueta para mostrar una task en el select.
     *              Ej: "Presión arterial — hoy 8:00 AR" o
     *                  "Glucemia — vencida (hace 2h)".
     *              TZ forzada a Argentina (regla 2.5 del workflow).
     */
    formatTaskOptionLabel(task) {
      if (!task.scheduledAt) return task.name;
      const date = new Date(task.scheduledAt);
      const ahora = new Date();
      const venceEn = date - ahora;
      const fecha = date.toLocaleString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      let suffix = ` — ${fecha} AR`;
      if (venceEn < 0) suffix += " (vencida)";
      return `${task.name}${suffix}`;
    },

    /**
     * @description Confirma el dispatch. Manda POST al endpoint, emite
     *              evento `dispatched` con los IDs y cierra el modal.
     *              El parent decide qué hacer (típicamente: refrescar
     *              el panel de auditoría para que aparezca la nueva
     *              Interaction).
     */
    async confirmDispatch() {
      if (!this.canDispatch) return;
      this.dispatching = true;
      try {
        const { data } = await whatsappConfigService.dispatchTask({
          patientId: this.selectedPatient._id,
          taskModel: this.taskModelFromType,
          taskId: this.selectedTaskId,
        });
        this.$message({
          type: "success",
          message: `Tarea disparada al paciente. Interaction: ${
            data.interactionId || data.conversationId
          }`,
          duration: 5000,
        });
        this.$emit("dispatched", data);
        this.close();
      } catch (e) {
        const msg =
          e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : "Error desconocido al disparar la tarea";
        this.$message.error(msg);
      } finally {
        this.dispatching = false;
      }
    },

    /**
     * @description Cierra el modal (emite update:visible para .sync).
     */
    close() {
      this.$emit("update:visible", false);
    },
  },
};
</script>

<template lang="pug">
  el-dialog.dispatch-modal(
    title="Disparar tarea manual al paciente vía WhatsApp"
    :visible="visible"
    @close="close"
    width="560px"
    :close-on-click-modal="!dispatching"
  )
    .dispatch-modal__form
      //- 1. Selector paciente
      .dispatch-modal__field
        label.dispatch-modal__label Paciente *
        el-select(
          v-model="selectedPatient"
          filterable
          remote
          :remote-method="onPatientSearch"
          :loading="patientLoading"
          placeholder="Buscar por nombre, apellido o teléfono..."
          value-key="_id"
          clearable
          style="width: 100%"
        )
          el-option(
            v-for="p in patientOptions"
            :key="p._id"
            :label="`${p.firstName} ${p.lastName} (${p.phone})`"
            :value="p"
          )

      //- 2. Tipo de tarea
      .dispatch-modal__field(v-if="selectedPatient")
        label.dispatch-modal__label Tipo de tarea *
        el-radio-group(v-model="taskType")
          el-radio(
            v-for="opt in taskTypeOptions"
            :key="opt.value"
            :label="opt.value"
          ) {{ opt.label }}

      //- 3. Tarea específica
      .dispatch-modal__field(v-if="taskType")
        label.dispatch-modal__label Tarea específica *
        el-select(
          v-model="selectedTaskId"
          :loading="tasksLoading"
          :placeholder="tasksLoading ? 'Cargando...' : 'Elegir tarea pendiente'"
          :no-data-text="tasksLoading ? 'Cargando...' : 'No hay tareas pendientes de este tipo'"
          style="width: 100%"
        )
          el-option(
            v-for="t in taskOptions"
            :key="t._id"
            :label="formatTaskOptionLabel(t)"
            :value="t._id"
          )

      //- Warning
      .dispatch-modal__warning(v-if="canDispatch")
        | ⚠ Esto manda un mensaje WhatsApp REAL al teléfono del paciente
        |  ({{ selectedPatient.phone }}). Asegurate del número antes de confirmar.

    span.dialog-footer(slot="footer")
      el-button(@click="close" :disabled="dispatching") Cancelar
      el-button(
        type="primary"
        :disabled="!canDispatch"
        :loading="dispatching"
        @click="confirmDispatch"
      ) Disparar al paciente
</template>

<style lang="scss" scoped>
.dispatch-modal {
  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  &__warning {
    background: #fffbeb;
    border-left: 3px solid #f59e0b;
    padding: 10px 14px;
    border-radius: 4px;
    font-size: 12px;
    color: #92400e;
    line-height: 1.4;
  }
}
</style>
